// src/features/hector-agent-casediscovery-v3/services/WebSocketClient.ts

import type { BackendMessage, FrontendMessage } from '../types';
import {
  RedactionPlanRequestSchema,
  TurnCatchupFrameSchema,
  buildWebSocketAuthProtocols,
} from '@hector/shared-types';
import { ServerFrameAgeEstimator, type LocalClocks } from './serverFrameAge';

const localClocks = (): LocalClocks => ({ wallMs: Date.now(), monoMs: performance.now() });

type MessageListener = (payload: unknown) => void;

// Minimum time (ms) a connection must stay open to be considered "stable"
// If connection closes faster than this, it's likely due to auth issues
const MIN_STABLE_CONNECTION_TIME = 3000;

// Custom close codes from backend
const CLOSE_AUTH_FAILED = 4001;
const CLOSE_NO_TOKEN = 4002;
const CLOSE_AUTH_TIMEOUT = 4003;

// Frontend heartbeat timeout: if no heartbeat from backend in 90s (6 missed),
// consider the connection dead. This catches cases where onclose doesn't fire
// (silent network failures) or where the backend's grace period expires without
// the frontend knowing — preventing sessions stuck in 'streaming' forever.
// Bumped 2026-05-28 from 45s → 90s (marge sous Render proxy idle + burst silence WS, voir audit 2026-05-28).
const HEARTBEAT_TIMEOUT_MS = 90_000;

/**
 * Chasse d'instance (#1816). `hector-backend` tourne sur plusieurs instances Render sans
 * sticky session ; un tour vit sur une seule, avec son tampon de rejeu. Quand la socket
 * courante apprend que le tour tourne AILLEURS, on ouvre plusieurs sockets candidates en
 * parallèle : celle que l'instance du tour adopte (`connection_established { resumed:true }`)
 * devient la socket courante et rejoue le tampon ; les autres sont fermées sans qu'aucune
 * de leurs trames ne remonte. Sans gagnante, la socket courante reste telle quelle (elle a
 * déjà armé côté backend l'attente de `turn_settled`).
 */
export interface TurnHuntOptions {
  /** Sockets ouvertes par salve. */
  candidates?: number;
  /** Salves successives sans gagnante avant d'abandonner. */
  rounds?: number;
  /**
   * Durée d'une salve : à l'échéance, ses candidates encore muettes (poignée de main qui
   * traîne sur une instance saturée) sont fermées et la salve suivante s'ouvre. Sans cette
   * borne, UNE candidate silencieuse suffisait à bloquer la seconde salve jusqu'au délai
   * total (revue 2026-09-29).
   */
  roundTimeoutMs?: number;
  /** Durée totale au-delà de laquelle la chasse est abandonnée. */
  timeoutMs?: number;
  /**
   * Les candidates demandent le rattrapage du tour (#1888). C'est le pool qui tranche :
   * seule une page qui a repris le tour au chargement en a besoin, et la gagnante est la
   * socket qui le recevra.
   */
  catchup?: boolean;
}

export type TurnHuntOutcome =
  /** Une candidate a été adoptée par l'instance du tour ; elle est la socket courante. */
  | 'won'
  /** Toutes les candidates ont répondu « hors de portée » (ou sont tombées). */
  | 'lost'
  /** Échéance, `abortHunt()` ou `disconnect()` : candidates fermées, socket courante intacte. */
  | 'aborted'
  /** Une chasse était déjà en cours sur ce client : aucune socket ouverte. */
  | 'skipped';

export interface TurnHuntResult {
  outcome: TurnHuntOutcome;
  /**
   * Pourquoi la chasse s'est arrêtée : `adopted`, `unadoptable` (une candidate a atteint
   * l'instance du tour ou appris que le tour est fini, mais rien n'était à adopter : socket
   * d'origine encore ouverte là-bas, tampon dépassé, tour terminé — inutile d'insister),
   * `exhausted` (toutes les salves jouées), `timeout`, `aborted`, `busy` (chasse déjà en cours).
   */
  reason: 'adopted' | 'unadoptable' | 'exhausted' | 'timeout' | 'aborted' | 'busy';
  /** Sockets candidates ouvertes au total. */
  attempts: number;
  durationMs: number;
  /** Instance gagnante (champ `instance` du verdict d'adoption). */
  instance: string | null;
  /**
   * Une candidate a reçu un refus portant `turn_owner_connected` : le tour est tenu par
   * une autre socket VIVANTE (autre onglet, autre appareil), personne n'a été déconnecté.
   * Seule l'instance du tour peut le dire ; `false` = aucune candidate ne l'a appris.
   */
  turnOwnerConnected: boolean;
}

/**
 * Défauts de la chasse. `candidates × rounds` = 42 essais : la probabilité de ne jamais
 * tomber sur l'instance du tour est (1 − 1/N)^42, N = taille de la flotte `hector-backend`
 * (réglée dans Render, cf. docs/architecture/iac/render.md) : ≈ 0,05 % à N = 6, négligeable
 * en deçà (cible fixée le 2026-10-01 : ≤ 0,1 %). Le cas courant s'arrête bien avant : une
 * adoption dès la première salve, ou une sortie anticipée quand une candidate apprend qu'il
 * n'y a rien à adopter (`reason: 'unadoptable'`). `roundTimeoutMs`
 * couvre une poignée de main complète (TLS + auth Supabase + 2 lectures OVH, 300–500 ms
 * observés) avec marge ; `timeoutMs` borne le tout bien en deçà de la fenêtre de rejeu
 * backend (5 min), le seul délai qui compte pour l'avocat étant la plume qui reprend.
 */
const TURN_HUNT_DEFAULTS: Required<TurnHuntOptions> = Object.freeze({
  candidates: 6,
  rounds: 7,
  roundTimeoutMs: 1_200,
  timeoutMs: 10_000,
  catchup: false,
});

type HuntCandidate = {
  ws: WebSocket;
  /** `true` dès que la candidate a répondu (verdict, chute, erreur) : elle ne compte plus. */
  settled: boolean;
};

export type AuthFailureCallback = () => void;
/** Why the connection closed — drives reconnection decisions in the hook */
export type ConnectionCloseReason = 'manual' | 'auth_failure' | 'unexpected';
export type ConnectionChangeCallback = (isConnected: boolean, reason?: ConnectionCloseReason) => void;

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private listeners = new Map<string, Set<MessageListener>>();
  private connectionOpenTime: number | null = null;
  private immediateCloseCount = 0;
  private maxImmediateCloses = 3;
  private isManualDisconnect = false;
  private receivedPendingAck = false;
  private onAuthFailure: AuthFailureCallback | null = null;
  private onConnectionChange: ConnectionChangeCallback | null = null;
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly frameAge = new ServerFrameAgeEstimator();
  private visibilityHandler: (() => void) | null = null;
  /** Chasse en cours : `null` sinon. Une seule à la fois par client. */
  private hunt: { abort: () => void } | null = null;

  constructor(
    private url: string,
    private token: string
  ) {}

  /**
   * Set callback for when auth failures stop reconnection.
   * This is called when too many immediate closes are detected (likely expired token).
   */
  setOnAuthFailure(callback: AuthFailureCallback | null): void {
    this.onAuthFailure = callback;
  }

  /**
   * Set callback for connection state changes.
   */
  setOnConnectionChange(callback: ConnectionChangeCallback | null): void {
    this.onConnectionChange = callback;
  }

  /**
   * The JWT travels in `Sec-WebSocket-Protocol`, never in the URL: Render's
   * request logs record the URL of every connection (#1919).
   */
  private openSocket(): WebSocket {
    return new WebSocket(this.url, buildWebSocketAuthProtocols(this.token));
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.ws = this.openSocket();
        this.isManualDisconnect = false;

        this.ws.onopen = () => {
          this.connectionOpenTime = Date.now();
          this.receivedPendingAck = false;
          this.resetHeartbeatTimer(); // Start watching for backend heartbeats
          this.onConnectionChange?.(true);
          resolve();
        };

        this.attachSocketHandlers(this.ws, reject);

        // WHY: detect tab return-to-foreground to recover from browser JS
        // throttling / freezing faster than the 45s heartbeat watchdog.
        this.setupVisibilityHandler();
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Handlers de la socket COURANTE (message, erreur, fermeture). Partagés par `connect()` et
   * par l'adoption d'une gagnante de chasse : une seule définition du protocole côté client.
   */
  private attachSocketHandlers(ws: WebSocket, onError: (error: Error) => void): void {
    ws.onmessage = (event) => {
      let message: Record<string, unknown>;
      try {
        message = JSON.parse(event.data);
      } catch {
        console.warn('[WS] Invalid JSON received, ignoring');
        return;
      }
      // Toutes les trames, battements compris : ce sont eux qui, toutes les 15 s,
      // tiennent à jour l'estimation du décalage d'horloge (cf. `serverFrameAge.ts`).
      this.frameAge.observe(typeof message.timestamp === 'string' ? message.timestamp : undefined, localClocks());

      // Fast path: respond to heartbeat immediately, don't propagate to listeners.
      // WHY: the backend sends JSON heartbeats every 15s to keep Render's reverse
      // proxy alive. We ACK immediately so the backend knows we're not dead.
      if (message.type === 'heartbeat') {
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ type: 'heartbeat_ack' }));
        }
        // Reset heartbeat watchdog — connection is alive
        this.resetHeartbeatTimer();
        return;
      }

      if (message.type === 'redaction_plan_request') {
        const parsed = RedactionPlanRequestSchema.safeParse(message);
        if (!parsed.success) {
          console.warn('[WS] Invalid redaction_plan_request received, ignoring', parsed.error.issues);
          return;
        }
        this.handleMessage(parsed.data as BackendMessage);
        return;
      }

      // Le rattrapage reconstruit toute la timeline du tour d'un coup (#1888) : une trame
      // malformée appliquée à moitié laisserait un tour troué. Elle est écartée ici, et le
      // tour reste ce qu'il était (direct seul, réponse complète à la fin du tour).
      if (message.type === 'turn_catchup') {
        const parsed = TurnCatchupFrameSchema.safeParse(message);
        if (!parsed.success) {
          console.warn('[WS] Invalid turn_catchup received, ignoring', parsed.error.issues);
          return;
        }
        this.handleMessage(parsed.data);
        return;
      }

      this.handleMessage(message as unknown as BackendMessage);
    };

    ws.onerror = (event) => {
      console.error('[WebSocketClient] Error', event);
      // Wrap the DOM Event in a proper Error so ErrorService can classify it correctly
      // (a raw Event object would produce "[object Event]" and bypass the network noise filter)
      onError(new Error('WebSocket network error'));
    };

    ws.onclose = (event) => {
      this.clearHeartbeatTimer();
      this.teardownVisibilityHandler();
      // WHY log every close: WS disconnections during JP searches and
      // context compaction are hard to diagnose. The close code tells us
      // whether it was the proxy (1006), the backend (1000/1001), or auth
      // (4001-4003). The reason string is often empty for proxy kills.
      const connectionDuration = this.connectionOpenTime
        ? Date.now() - this.connectionOpenTime
        : 0;
      console.warn(
        `[WebSocketClient] Connection closed — code=${event.code} reason="${event.reason || '(none)'}" clean=${event.wasClean} duration=${connectionDuration}ms`,
      );

      if (this.isManualDisconnect) {
        this.onConnectionChange?.(false, 'manual');
        return;
      }

      // Custom auth close codes from backend
      if (event.code === CLOSE_AUTH_FAILED || event.code === CLOSE_NO_TOKEN || event.code === CLOSE_AUTH_TIMEOUT) {
        console.error(`[WebSocketClient] Auth failure (code ${event.code}): ${event.reason}`);
        this.onConnectionChange?.(false, 'auth_failure');
        this.onAuthFailure?.();
        return;
      }

      // Server acknowledged us — not an auth issue
      if (this.receivedPendingAck) {
        this.immediateCloseCount = 0;
        this.onConnectionChange?.(false, 'unexpected');
        return;
      }

      // Immediate close detection (likely auth failure with expired token)
      if (connectionDuration < MIN_STABLE_CONNECTION_TIME) {
        this.immediateCloseCount++;
        console.warn(`[WebSocketClient] Immediate close detected (${connectionDuration}ms) - count: ${this.immediateCloseCount}/${this.maxImmediateCloses}`);

        if (this.immediateCloseCount >= this.maxImmediateCloses) {
          console.error('[WebSocketClient] Too many immediate closes (likely auth issue)');
          this.onConnectionChange?.(false, 'auth_failure');
          this.onAuthFailure?.();
          return;
        }
      } else {
        this.immediateCloseCount = 0;
      }

      this.onConnectionChange?.(false, 'unexpected');
    };
  }

  /**
   * Chasse d'instance (#1816) : voir {@link TurnHuntOptions}. Résout toujours, jamais de
   * rejet : une chasse perdue n'est pas une erreur, la socket courante reste valide.
   */
  huntForTurn(conversationId: string, options: TurnHuntOptions = {}): Promise<TurnHuntResult> {
    if (this.hunt) return Promise.resolve({ outcome: 'skipped', reason: 'busy', attempts: 0, durationMs: 0, instance: null, turnOwnerConnected: false });
    const { candidates, rounds, roundTimeoutMs, timeoutMs, catchup } = { ...TURN_HUNT_DEFAULTS, ...options };
    const startedAt = Date.now();
    let attempts = 0;
    /** Candidates de la salve COURANTE ; celles des salves précédentes sont déjà fermées. */
    let roundCandidates: HuntCandidate[] = [];
    let round = 0;
    /** Une candidate a appris qu'il n'y a rien à adopter : plus de salve après celle-ci. */
    let unadoptable = false;
    let turnOwnerConnected = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let roundTimer: ReturnType<typeof setTimeout> | null = null;

    return new Promise<TurnHuntResult>((resolve) => {
      let done = false;
      const finish = (outcome: TurnHuntOutcome, reason: TurnHuntResult['reason'], instance: string | null): void => {
        if (done) return;
        done = true;
        if (timer) clearTimeout(timer);
        if (roundTimer) clearTimeout(roundTimer);
        // Dégradation acceptée (revue 2026-09-30), chemins abandon/échéance seulement : si
        // l'instance du tour a DÉJÀ adopté une candidate (`resumed:true` envoyé, tampon
        // rejoué côté backend, grâce effacée) mais que ces trames sont encore en vol quand
        // cette fermeture arrive — fenêtre d'une latence aller simple —, elles sont perdues
        // et le backend repart en grâce avec un tampon vide. La base reste la vérité
        // (rechargée à `turn_settled` par la socket courante, qui surveille le verrou).
        for (const c of roundCandidates) this.closeSilently(c.ws);
        roundCandidates = [];
        this.hunt = null;
        resolve({ outcome, reason, attempts, durationMs: Date.now() - startedAt, instance, turnOwnerConnected });
      };
      this.hunt = { abort: () => finish('aborted', 'aborted', null) };
      timer = setTimeout(() => finish('aborted', 'timeout', null), timeoutMs);

      const nextRoundOrLost = (): void => {
        if (roundTimer) clearTimeout(roundTimer);
        roundTimer = null;
        if (unadoptable) finish('lost', 'unadoptable', null);
        else if (round < rounds) openRound();
        else finish('lost', 'exhausted', null);
      };

      const settle = (candidate: HuntCandidate): void => {
        if (candidate.settled) return;
        candidate.settled = true;
        this.closeSilently(candidate.ws);
        if (roundCandidates.every((c) => c.settled)) nextRoundOrLost();
      };

      const adopt = (candidate: HuntCandidate, instance: string | null): void => {
        candidate.settled = true;
        // La gagnante sort de la salve AVANT `finish`, qui ferme tout ce qui y reste.
        roundCandidates = roundCandidates.filter((c) => c !== candidate);
        this.adoptSocket(candidate.ws);
        finish('won', 'adopted', instance);
      };

      const openRound = (): void => {
        round += 1;
        roundCandidates = [];
        roundTimer = setTimeout(() => {
          // Les muettes de cette salve ne compteront plus : fermées, puis salve suivante.
          for (const c of roundCandidates) {
            if (!c.settled) {
              c.settled = true;
              this.closeSilently(c.ws);
            }
          }
          nextRoundOrLost();
        }, roundTimeoutMs);
        for (let i = 0; i < candidates; i++) {
          let ws: WebSocket;
          try {
            ws = this.openSocket();
          } catch {
            continue;
          }
          attempts += 1;
          const candidate: HuntCandidate = { ws, settled: false };
          roundCandidates.push(candidate);
          // Volontairement rien à `open` : côté backend le 101 précède l'authentification,
          // une trame envoyée avant l'ACK générique se perd sans erreur (voir `onmessage`).
          ws.onerror = () => settle(candidate);
          ws.onclose = () => settle(candidate);
          ws.onmessage = (event) => {
            let message: Record<string, unknown>;
            try {
              message = JSON.parse(event.data);
            } catch {
              return;
            }
            if (message.type === 'heartbeat') {
              if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'heartbeat_ack' }));
              return;
            }
            if (message.type !== 'connection_established') return;
            if (message.resumed === undefined) {
              // ACK générique : le backend écoute (une trame envoyée à `open` se perdrait).
              ws.send(JSON.stringify({
                type: 'session_resume',
                conversation_id: conversationId,
                ...(catchup ? { catchup: true } : {}),
              }));
              return;
            }
            if (message.resumed === true) {
              adopt(candidate, typeof message.instance === 'string' ? message.instance : null);
              return;
            }
            // `resumed:false`. Deux verdicts disent que les salves SUIVANTES sont vaines :
            // le tour est fini (pas de `turn_in_progress`), ou cette candidate a atteint
            // l'instance du tour sans rien à adopter (`instance === turn_instance` : socket
            // d'origine encore ouverte là-bas, tampon dépassé… ou une SŒUR de cette salve
            // vient d'adopter la grâce une milliseconde avant elle). Ce dernier cas interdit
            // de terminer la chasse ici : la trame `resumed:true` de la sœur peut être en
            // vol, et `finish` fermerait la gagnante avec son tampon. On laisse donc la
            // salve en cours aller au bout ; sans gagnante, `lost / unadoptable`.
            const reachedTurnInstance = typeof message.instance === 'string'
              && typeof message.turn_instance === 'string'
              && message.instance === message.turn_instance;
            if (message.turn_in_progress !== true || reachedTurnInstance) unadoptable = true;
            if (message.turn_owner_connected === true) turnOwnerConnected = true;
            settle(candidate);
          };
        }
        if (roundCandidates.length === 0) finish('lost', 'exhausted', null);
      };

      openRound();
    });
  }

  /** Abandonne la chasse en cours, s'il y en a une. Sans effet sinon. */
  abortHunt(): void {
    this.hunt?.abort();
  }

  /**
   * La gagnante remplace la socket courante. L'ancienne est fermée SANS ses handlers : côté
   * backend, sa fermeture retire l'attente de `turn_settled` ; côté client, son `onclose` ne
   * doit surtout pas passer pour une coupure (le pool relancerait une reconnexion).
   */
  private adoptSocket(ws: WebSocket): void {
    const previous = this.ws;
    this.ws = ws;
    this.connectionOpenTime = Date.now();
    this.receivedPendingAck = true;
    this.immediateCloseCount = 0;
    this.attachSocketHandlers(ws, (error) => console.error('[WebSocketClient] adopted socket error', error));
    this.resetHeartbeatTimer();
    if (previous && previous !== ws) this.closeSilently(previous);
  }

  /** Ferme une socket sans qu'aucun de ses handlers ne se déclenche. */
  private closeSilently(ws: WebSocket): void {
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      try {
        ws.close();
      } catch {
        // Une socket déjà morte ne doit pas faire échouer la chasse.
      }
    }
  }

  private handleMessage(message: BackendMessage): void {
    // Server acknowledged connection, auth in progress — reset immediate close detection
    if (message.type === 'connection_pending') {
      this.receivedPendingAck = true;
      this.immediateCloseCount = 0;
    }

    // Fully authenticated — reset failure counters
    if (message.type === 'connection_established') {
      this.receivedPendingAck = true;
      this.immediateCloseCount = 0;
    }

    // Notify type-specific listeners
    const listeners = this.listeners.get(message.type);
    if (listeners) {
      // Personal-skill frames intentionally follow the shared exact top-level
      // contract; older CD frames expose their domain body under `payload`.
      const payload = 'payload' in message ? message.payload : message;
      listeners.forEach(listener => listener(payload));
    }

    // Notify wildcard listeners
    const allListeners = this.listeners.get('*');
    if (allListeners) {
      allListeners.forEach(listener => listener(message));
    }
  }

  on(messageType: string, listener: MessageListener): void {
    if (!this.listeners.has(messageType)) {
      this.listeners.set(messageType, new Set());
    }
    this.listeners.get(messageType)!.add(listener);
  }

  off(messageType: string, listener: MessageListener): void {
    const listeners = this.listeners.get(messageType);
    if (listeners) {
      listeners.delete(listener);
    }
  }

  send(message: FrontendMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.error('[WebSocketClient] Cannot send - not connected');
    }
  }

  disconnect(): void {
    this.abortHunt();
    this.isManualDisconnect = true;
    this.clearHeartbeatTimer();
    this.teardownVisibilityHandler();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Retard d'une trame déjà reçue (son `timestamp` serveur) au moment présent,
   * horloge du poste corrigée. `null` tant qu'aucune trame n'a permis l'estimation.
   */
  serverFrameAgeMs(timestamp: string | undefined): number | null {
    return this.frameAge.ageMs(timestamp, localClocks());
  }

  /** Reset the heartbeat watchdog timer. Called on each heartbeat received. */
  private resetHeartbeatTimer(): void {
    this.clearHeartbeatTimer();
    this.heartbeatTimer = setTimeout(() => {
      // 3 missed heartbeats (45s) — connection is dead.
      // WHY force-close instead of just notifying: the WS may be in a half-open
      // state where onclose never fires (e.g. silent network drop, HMR reload).
      // Closing the socket triggers the normal onclose → reconnection flow.
      console.warn('[WS] Heartbeat timeout — no heartbeat for 45s, closing connection');
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.close(4000, 'Heartbeat timeout (client-side)');
      } else {
        // WS already in CLOSING/CLOSED state but onclose hasn't fired yet
        this.onConnectionChange?.(false, 'unexpected');
      }
    }, HEARTBEAT_TIMEOUT_MS);
  }

  private setupVisibilityHandler(): void {
    // Idempotent: remove stale handler from a previous cycle.
    // WHY: attemptReconnect creates a new WebSocketClient and calls connect(),
    // so we re-enter here. The old instance's disconnect() cleans its own
    // listener, but guard idempotence belt-and-braces.
    this.teardownVisibilityHandler();

    this.visibilityHandler = () => {
      if (document.visibilityState !== 'visible') return;
      if (!this.ws) return;

      if (this.ws.readyState === WebSocket.CLOSED || this.ws.readyState === WebSocket.CLOSING) {
        // WS already dead while we were hidden — nothing to do here, onclose
        // handler will have already notified (or will shortly). No need to
        // force-notify and risk false-positive immediateCloseCount.
        console.warn('[WS] Tab visible again but socket is dead — onclose flow will reconnect');
        return;
      }

      if (this.ws.readyState === WebSocket.OPEN) {
        // Connection may look alive but JS was frozen during the hidden
        // period — we may have missed heartbeats. Send a proactive ACK to
        // reset the server's inactivity tracking, and restart our own
        // watchdog. If the TCP connection is half-open (peer unreachable),
        // the send will fail synchronously and we force-close for a fast
        // reconnect via the standard onclose flow.
        try {
          this.ws.send(JSON.stringify({ type: 'heartbeat_ack' }));
        } catch (err) {
          console.warn('[WS] heartbeat_ack on visibility change failed — forcing close', err);
          // Send failed → socket unusable → force close. Code 4000 reuses the
          // existing "Heartbeat timeout (client-side)" flow. receivedPendingAck
          // set earlier keeps immediateCloseCount sane.
          this.ws.close(4000, 'Visibility wake: send failed');
          return;
        }
        this.resetHeartbeatTimer();
      }
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private teardownVisibilityHandler(): void {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  private clearHeartbeatTimer(): void {
    if (this.heartbeatTimer) {
      clearTimeout(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

}
